import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { usePrefs, formatPrice } from "@/context/PreferencesContext";
import { toast } from "sonner";
import { ShoppingBag } from "@phosphor-icons/react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { add } = useCart();
  const { currency } = usePrefs();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      const sizes = [...new Set((r.data.variants || []).map((v) => v.size).filter(Boolean))];
      const colors = [...new Set((r.data.variants || []).map((v) => v.color).filter(Boolean))];
      if (sizes.length) setSize(sizes[0]);
      if (colors.length) setColor(colors[0]);
    });
  }, [id]);

  if (!product) return <div className="p-20 text-center uppercase tracking-widest text-white/50">Chargement...</div>;

  const sizes = [...new Set((product.variants || []).map((v) => v.size).filter(Boolean))];
  const colors = [...new Set((product.variants || []).map((v) => v.color).filter(Boolean))];
  const images = product.images?.length ? product.images : [null];

  const variantStock = (product.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
  const effectiveStock = variantStock > 0 ? variantStock : (product.stock || 0);
  const outOfStock = effectiveStock === 0;

  const addToCart = () => {
    if (sizes.length > 0 && !size) return toast.error("Choisissez une taille");
    add({
      product_id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0],
      artist_name: product.artist_name,
      quantity: qty, size, color,
    });
    toast.success("Ajouté au panier");
  };

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="product-page">
      <div className="grid lg:grid-cols-2">
        {/* Images */}
        <div className="border-r border-white/10 lg:sticky lg:top-16 lg:self-start">
          <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
            {images[activeImg] ? (
              <img src={fileUrl(images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-8xl text-white/10">MERCH</div>
            )}
            {product.limited_edition && (
              <div className="absolute top-6 left-6 bg-[#FF3B00] text-white text-xs uppercase tracking-widest px-3 py-1.5">Édition Limitée</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 p-4 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 border ${activeImg === i ? "border-[#FF3B00]" : "border-white/10"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Details */}
        <div className="p-8 lg:p-16">
          <Link to={`/pilotes/${product.artist_id}`} className="text-xs uppercase tracking-[0.3em] text-[#FF3B00] hover:underline">
            {product.artist_name}
          </Link>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-none mt-2">{product.name}</h1>
          <div className="mt-4 font-display text-4xl">{formatPrice(product.price || 0, currency).display} <span className="text-sm text-white/40 tracking-widest align-middle">{formatPrice(0, currency).suffix}</span></div>
          <p className="mt-6 text-white/70 whitespace-pre-wrap">{product.description}</p>

          {sizes.length > 0 && (
            <div className="mt-8">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Taille</div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`w-14 h-14 border ${size === s ? "bg-[#FF3B00] border-[#FF3B00] text-white" : "border-white/20 hover:border-white"} uppercase text-sm`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {colors.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Couleur</div>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)}
                    className={`px-4 h-12 border ${color === c ? "bg-[#FF3B00] border-[#FF3B00] text-white" : "border-white/20 hover:border-white"} uppercase text-xs`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-white/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-14 hover:bg-white/5">−</button>
              <div className="w-14 text-center">{qty}</div>
              <button onClick={() => setQty(qty + 1)} className="w-12 h-14 hover:bg-white/5">+</button>
            </div>
            <button onClick={addToCart} data-testid="add-to-cart" disabled={outOfStock}
              className="flex-1 py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
              <ShoppingBag size={16} /> {outOfStock ? "Rupture" : "Ajouter au panier"}
            </button>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40">Stock</div>
              <div className="mt-1">{effectiveStock} unités</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40">Expédition</div>
              <div className="mt-1">Directement par le pilote</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
